import { useEffect, useState } from 'react';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { useProjectTimeline } from '@/hooks/useProjectTimeline';
import { usePreviewPlayback } from '../preview/usePreviewPlayback';
import { PreviewPlayer } from '../preview/PreviewPlayer';
import { Timeline } from '../timeline/Timeline';
import { Tabs } from '@/components/Tabs';
import { ImagesPanel } from '../media/ImagesPanel';
import { VoiceoverPanel } from '../voiceover/VoiceoverPanel';
import { CaptionSettingsPanel } from '../captions/CaptionSettingsPanel';
import { MusicPanel } from '../audio/MusicPanel';
import { WatermarkPanel } from '../watermark/WatermarkPanel';
import { ExportPanel } from '../export/ExportPanel';
import { ProjectSettingsPanel } from './ProjectSettingsPanel';
import type { ReelProject } from '@/types';

const TABS = [
  { id: 'settings', label: 'Settings' },
  { id: 'media', label: 'Media' },
  { id: 'voiceover', label: 'Voiceover' },
  { id: 'captions', label: 'Captions' },
  { id: 'music', label: 'Music' },
  { id: 'watermark', label: 'Watermark' },
  { id: 'export', label: 'Export' },
];

export function ReelWorkspace({ project }: { project: ReelProject }) {
  const shared = useWorkspaceStore((s) => s.workspace.shared);
  const updateCaptions = useWorkspaceStore((s) => s.updateCaptions);
  const updateWatermark = useWorkspaceStore((s) => s.updateWatermark);
  const [activeTab, setActiveTab] = useState('settings');

  const timeline = useProjectTimeline(project);
  const playback = usePreviewPlayback(timeline.totalDurationSeconds);

  // Listen for drag events dispatched by PreviewPlayer's draggable overlays.
  useEffect(() => {
    function onDrag(e: Event) {
      const detail = (e as CustomEvent).detail as { target: 'caption' | 'watermark'; xPercent: number; yPercent: number; projectId: string };
      if (detail.projectId !== project.id) return;
      if (detail.target === 'caption') {
        const rect = project.captions.customRect;
        updateCaptions(project.id, { customRect: { ...rect, x: Math.min(1 - rect.width, detail.xPercent), y: Math.min(1 - rect.height, detail.yPercent) } });
      } else {
        updateWatermark(project.id, {
          positionPreset: 'custom',
          offsetXPercent: detail.xPercent * 100,
          offsetYPercent: detail.yPercent * 100,
        });
      }
    }
    window.addEventListener('reels:drag-position', onDrag);
    return () => window.removeEventListener('reels:drag-position', onDrag);
  }, [project, updateCaptions, updateWatermark]);

  return (
    <div className="flex h-full flex-1 overflow-hidden">
      <div className="flex-1 overflow-y-auto p-4">
        <Tabs items={TABS} activeId={activeTab} onChange={setActiveTab} />
        <div className="mt-4">
          {activeTab === 'settings' && <ProjectSettingsPanel project={project} />}
          {activeTab === 'media' && <ImagesPanel project={project} />}
          {activeTab === 'voiceover' && <VoiceoverPanel project={project} />}
          {activeTab === 'captions' && <CaptionSettingsPanel project={project} />}
          {activeTab === 'music' && <MusicPanel project={project} />}
          {activeTab === 'watermark' && <WatermarkPanel project={project} />}
          {activeTab === 'export' && <ExportPanel project={project} />}
        </div>
      </div>

      <div className="w-[400px] shrink-0 space-y-3 overflow-y-auto border-l border-slate-200 p-4 dark:border-slate-800">
        <PreviewPlayer project={project} shared={shared} playback={playback} />
        <Timeline
          timeline={timeline}
          currentTime={playback.currentTime}
          isPlaying={playback.isPlaying}
          onSeek={playback.seek}
          onTogglePlay={playback.togglePlay}
          onStep={playback.step}
        />
      </div>
    </div>
  );
}
